import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components';
import OpenNewTabRoundedIcon from '@/shared/assets/open-new-tab-rounded-icon.svg?react';
import CandlesIcon from '@/shared/assets/candles-icon.svg?react';
import ArrowDownUpIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import ApiTokensIcon from '@/shared/assets/api-icon.svg?react';
import PlusIcon from '@/shared/assets/plus-icon.svg?react';
import { openTradingAccUrl, useMediaQuery } from '@/shared/lib';
import { cn } from '@/shared/utils';
import type {
  AccountMarginRest,
  AccountRest,
  AccountType,
} from '@/entities/account';
import { useAccountRows } from '../model/useAccountRows';
import { getFitSideOffset } from '../lib/getFitSideOffset';
import { AccountRowContent } from './AccountRowContent';
import { ActionMenuItem } from './ActionMenuItem';
import { MenuSeparator } from './MenuSeparator';
import {
  MENU_CONTENT_CLASSNAME,
  MENU_ITEM_CLASSNAME,
  MENU_ITEM_CONTENT_CLASSNAME,
  MENU_ITEM_ICON_CLASSNAME,
  MENU_ITEM_LABEL_CLASSNAME,
  MENU_VIEWPORT_CLASSNAME,
} from './menuClassNames';

const VISIBLE_ASSET_ICONS_QTY = 3;

const ACCOUNT_MENU_WIDTH_REM = 16.6;

const ACCOUNT_MENU_SIDE_OFFSET = 9;

const ACCOUNT_MENU_LABEL_CLASSNAME = cn(
  MENU_ITEM_LABEL_CLASSNAME,
  'whitespace-nowrap'
);

type Props = {
  type: AccountType;
  accounts: Array<AccountRest | AccountMarginRest>;
  currency: string;
  precision: number;
  isBalancesHidden: boolean;
  onTransfer: (accountNumber: string) => void;
  onAccountClick: (accountNumber: string) => void;
  onAddAccount?: () => void;
};

const AccountsListPopover = ({
  type,
  accounts,
  currency,
  precision,
  isBalancesHidden,
  onTransfer,
  onAccountClick,
  onAddAccount,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [open, setOpen] = useState(false);
  const [menuSideOffset, setMenuSideOffset] = useState(
    ACCOUNT_MENU_SIDE_OFFSET
  );
  const rows = useAccountRows(accounts);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-[0.2rem] py-[0.2rem] pl-[0.2rem] pr-[0.4rem] rounded-[2.2rem] cursor-pointer transition-colors hover:bg-secondary-bg data-[state=open]:bg-secondary-bg">
          <span className="flex items-center">
            {rows.slice(0, VISIBLE_ASSET_ICONS_QTY).map((row, index) => (
              <span
                key={row.accNum}
                className={cn(
                  'size-[2rem] rounded-full bg-secondary-bg overflow-hidden border border-background',
                  index > 0 && 'ml-[-0.6rem]'
                )}>
                {row.iconUrl && (
                  <img
                    src={row.iconUrl}
                    alt={row.assetCode}
                    className="size-full object-cover"
                  />
                )}
              </span>
            ))}
          </span>

          <ChevronDownIcon
            className={cn(
              'size-[1.6rem] text-tertiary transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        sticky="always"
        className="flex flex-col gap-[0.8rem] w-[23.6rem] max-h-(--radix-popover-content-available-height) overflow-y-auto p-[0.4rem] rounded-[1.2rem] border-stroke bg-background/94 backdrop-blur-[0.8rem] shadow-(--shadow-dropdown)!">
        {rows.map(row => (
          <DropdownMenu key={row.accNum}>
            <DropdownMenuTrigger
              withChevron={false}
              onPointerDown={e =>
                setMenuSideOffset(
                  isMobile
                    ? getFitSideOffset(
                        e.currentTarget,
                        ACCOUNT_MENU_WIDTH_REM,
                        ACCOUNT_MENU_SIDE_OFFSET
                      )
                    : ACCOUNT_MENU_SIDE_OFFSET
                )
              }
              className="w-full h-[3.6rem] shrink-0 items-center [&>div]:flex-1 [&>div]:min-w-0 px-[1.2rem] py-[0.8rem] rounded-[0.8rem] cursor-pointer hover:bg-secondary-bg data-[state=open]:bg-secondary-bg">
              <AccountRowContent
                row={row}
                currency={currency}
                precision={precision}
                isBalancesHidden={isBalancesHidden}>
                <ChevronRightIcon className="size-[1.6rem] text-tertiary shrink-0" />
              </AccountRowContent>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side="right"
              align="start"
              sideOffset={menuSideOffset}
              alignOffset={-5}
              collisionPadding={16}
              className={cn(MENU_CONTENT_CLASSNAME, 'w-max min-w-[16.6rem]')}
              classNameViewport={MENU_VIEWPORT_CLASSNAME}>
              <ActionMenuItem
                icon={CandlesIcon}
                label={t('personalAccount.overview.actions.trade')}
                className={MENU_ITEM_CLASSNAME}
                childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
                iconClassName={MENU_ITEM_ICON_CLASSNAME}
                labelClassName={ACCOUNT_MENU_LABEL_CLASSNAME}
                onClick={() => openTradingAccUrl(row.tradingAccKey, type)}>
                <OpenNewTabRoundedIcon className="size-[1.2rem] text-tertiary shrink-0" />
              </ActionMenuItem>
              <ActionMenuItem
                icon={ArrowDownUpIcon}
                label={t('personalAccount.overview.actions.transfer')}
                className={MENU_ITEM_CLASSNAME}
                childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
                iconClassName={MENU_ITEM_ICON_CLASSNAME}
                labelClassName={ACCOUNT_MENU_LABEL_CLASSNAME}
                onClick={() => onTransfer(row.accNum)}
              />
              <MenuSeparator />
              <ActionMenuItem
                icon={ApiTokensIcon}
                label={t('personalAccount.overview.actions.webApiTokens')}
                className={MENU_ITEM_CLASSNAME}
                childrenClassName={MENU_ITEM_CONTENT_CLASSNAME}
                iconClassName={MENU_ITEM_ICON_CLASSNAME}
                labelClassName={ACCOUNT_MENU_LABEL_CLASSNAME}
                onClick={() => onAccountClick(row.accNum)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        ))}

        {onAddAccount && (
          <>
            <MenuSeparator />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onAddAccount();
              }}
              className="flex h-[3.6rem] shrink-0 items-center gap-[0.8rem] px-[1.2rem] py-[0.8rem] rounded-[0.8rem] medium5 text-primary cursor-pointer hover:bg-secondary-bg">
              <PlusIcon className="size-[1.6rem] shrink-0" />
              {t('personalAccount.overview.actions.addAccount')}
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export { AccountsListPopover };
